import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, ChevronRight, WashingMachine, PackageSearch, UserRound, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { NAV_LINKS } from "@/components/nav/links";
import { BUSINESS } from "@/lib/legal";

const itemClass =
  "w-full flex items-center justify-between px-4 py-3 rounded-xl text-slate-700 font-medium transition-colors duration-150 hover:bg-cyan-50 hover:text-cyan-700";

export const MobileMenu = ({ goTo }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const go = (anchor) => {
    setOpen(false);
    setTimeout(() => goTo(anchor), 150);
  };

  const doLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" data-testid="nav-mobile-toggle" aria-label="Abrir menú"
          className="lg:hidden rounded-full text-slate-700 hover:bg-cyan-50 hover:text-cyan-700">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[86vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu">
        <SheetHeader className="p-5 border-b border-slate-100 text-left">
          <SheetTitle className="flex items-center gap-2 font-heading text-slate-900">
            <span className="bg-gradient-to-br from-cyan-400 to-cyan-600 text-white p-1.5 rounded-lg">
              <WashingMachine className="h-4 w-4" />
            </span>
            Lavandería Ozono
          </SheetTitle>
        </SheetHeader>
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5" aria-label="Menú móvil">
          {NAV_LINKS.map((l) => (
            <button key={l.anchor} type="button" data-testid={`mobile-nav-${l.anchor}`}
              onClick={() => go(l.anchor)} className={itemClass}>
              {l.label} <ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
          <Link to="/seguimiento" onClick={() => setOpen(false)} data-testid="mobile-nav-seguimiento" className={itemClass}>
            <span className="flex items-center gap-2"><PackageSearch className="h-4 w-4 text-cyan-600" /> Seguimiento de pedido</span>
            <ChevronRight className="h-4 w-4 text-slate-300" />
          </Link>
          <div className="my-3 border-t border-slate-100" />
          {user ? (
            <>
              <Link to="/cuenta/panel" onClick={() => setOpen(false)} data-testid="mobile-nav-account" className={itemClass}>
                <span className="flex items-center gap-2"><UserRound className="h-4 w-4 text-cyan-600" /> Mi cuenta</span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </Link>
              <button type="button" onClick={doLogout} data-testid="mobile-nav-logout" className={`${itemClass} text-red-600 hover:text-red-700 hover:bg-red-50`}>
                <span className="flex items-center gap-2"><LogOut className="h-4 w-4" /> Cerrar sesión</span>
              </button>
            </>
          ) : (
            <Link to="/cuenta" onClick={() => setOpen(false)} data-testid="mobile-nav-login" className={itemClass}>
              <span className="flex items-center gap-2"><UserRound className="h-4 w-4 text-cyan-600" /> Iniciar sesión</span>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </Link>
          )}
        </nav>
        <div className="p-5 border-t border-slate-100 space-y-3">
          <Button asChild data-testid="mobile-nav-reservar"
            className="w-full h-11 rounded-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 shadow-md shadow-cyan-500/30">
            <Link to="/reservar" onClick={() => setOpen(false)}>Reservar Recogida</Link>
          </Button>
          <p className="text-xs text-slate-500 text-center">{BUSINESS.hours} · {BUSINESS.phone}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
